/*
 * Amazon-style add to cart on product cards (snippets/card-atc.liquid).
 * One shared cart snapshot drives every <card-atc> on the page, so the same
 * product shown twice (best sellers + all products) always shows one number.
 */
(() => {
  if (customElements.get('card-atc')) return;

  const DEBOUNCE_MS = 350;
  const store = { cart: null, pending: null };

  const fetchCart = () => {
    store.pending = fetch(`${theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        store.cart = cart;
        syncAll();
        return cart;
      })
      .catch((e) => console.error(e));
    return store.pending;
  };

  const lineFor = (variantId) => {
    if (!store.cart) return { qty: 0, key: null };
    const lines = store.cart.items.filter((item) => String(item.variant_id) === String(variantId));
    return {
      qty: lines.reduce((sum, item) => sum + item.quantity, 0),
      key: lines.length ? lines[0].key : null,
      lineQty: lines.length ? lines[0].quantity : 0
    };
  };

  const syncAll = () => {
    document.querySelectorAll('card-atc').forEach((el) => el.sync && el.sync());
  };

  const miniCart = () => document.querySelector('mini-cart');

  const sectionsPayload = () => {
    const cart = miniCart();
    return cart ? cart.getSectionsToRender().map((section) => section.section) : [];
  };

  // Refresh the header bubble and drawer contents without opening the drawer
  // (cart-drawer.js decides whether to open based on the theme setting).
  const renderMiniCart = (parsedState) => {
    const cart = miniCart();
    if (cart && parsedState.sections) cart.renderContents(parsedState);
  };

  class CardAtc extends HTMLElement {
    connectedCallback() {
      this.variantId = this.dataset.variantId;
      this.max = this.dataset.max !== undefined ? parseInt(this.dataset.max, 10) : null;
      this.addButton = this.querySelector('[data-atc-add]');
      this.stepper = this.querySelector('[data-atc-stepper]');
      this.minusButton = this.querySelector('[data-atc-minus]');
      this.plusButton = this.querySelector('[data-atc-plus]');
      this.qtyEl = this.querySelector('[data-atc-qty]');
      this.errorEl = this.querySelector('[data-atc-error]');
      this.target = null;
      this.busy = false;

      this.addButton.addEventListener('click', this.onAdd.bind(this));
      this.plusButton.addEventListener('click', () => this.step(1));
      this.minusButton.addEventListener('click', () => this.step(-1));

      if (store.cart) this.sync();
    }

    // Shows the cart's real quantity unless the shopper is mid-tap.
    sync() {
      if (this.busy || this.target !== null) return;
      this.render(lineFor(this.variantId).qty);
    }

    render(qty) {
      const inCart = qty > 0;
      this.addButton.hidden = inCart;
      this.stepper.hidden = !inCart;
      this.classList.toggle('is-in-cart', inCart);
      if (!inCart) return;

      this.qtyEl.textContent = qty;
      const isLast = qty === 1;
      this.minusButton.classList.toggle('is-trash', isLast);
      this.minusButton.setAttribute(
        'aria-label',
        (isLast ? this.dataset.labelRemove : this.dataset.labelMinus) || ''
      );
      const atMax = this.max !== null && qty >= this.max;
      this.plusButton.disabled = atMax;
    }

    showError() {
      this.errorEl.hidden = false;
      clearTimeout(this.errorTimer);
      this.errorTimer = setTimeout(() => (this.errorEl.hidden = true), 3000);
    }

    onAdd() {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-loading');
      this.addButton.setAttribute('aria-busy', 'true');
      // Optimistic: flip to the stepper right away, like Amazon.
      this.render(lineFor(this.variantId).qty + 1);

      const body = JSON.stringify({
        items: [{ id: Number(this.variantId), quantity: 1 }],
        sections: sectionsPayload(),
        sections_url: window.location.pathname
      });

      fetch(theme.routes.cart_add_url, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status) {
            this.showError();
            return;
          }
          renderMiniCart(parsedState);
          document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.busy = false;
          this.classList.remove('is-loading');
          this.addButton.removeAttribute('aria-busy');
          fetchCart();
        });
    }

    // Taps are collected for a moment and sent as one change, so tapping +
    // three times quickly is one request for "4", not three racing requests.
    step(delta) {
      const current = this.target !== null ? this.target : lineFor(this.variantId).qty;
      let next = Math.max(0, current + delta);
      if (this.max !== null && next > this.max) {
        this.showError();
        return;
      }
      this.target = next;
      this.render(next);
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.commit(), next === 0 ? 0 : DEBOUNCE_MS);
    }

    commit() {
      if (this.busy) {
        this.debounceTimer = setTimeout(() => this.commit(), DEBOUNCE_MS);
        return;
      }
      const line = lineFor(this.variantId);
      const wanted = this.target;
      if (wanted === null || !line.key) {
        this.target = null;
        this.sync();
        return;
      }
      // Other lines of the same variant (e.g. with properties) stay as they are.
      const lineQuantity = Math.max(0, wanted - (line.qty - line.lineQty));

      this.busy = true;
      this.classList.add('is-loading');
      const body = JSON.stringify({
        id: line.key,
        quantity: lineQuantity,
        sections: sectionsPayload(),
        sections_url: window.location.pathname
      });

      fetch(theme.routes.cart_change_url, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status) {
            this.showError();
            return;
          }
          renderMiniCart(parsedState);
          const { sections, ...cart } = parsedState;
          store.cart = cart;
        })
        .catch((e) => console.error(e))
        .finally(() => {
          this.busy = false;
          this.classList.remove('is-loading');
          if (this.target === wanted) this.target = null;
          if (this.target === null) {
            syncAll();
            if (!store.cart || lineFor(this.variantId).qty !== wanted) fetchCart();
          }
        });
    }
  }

  customElements.define('card-atc', CardAtc);

  // "Upgrade to a triple" inside the cart (snippets/cart-pack-upgrade.liquid).
  // One /cart/update call swaps the singles for the pack, so the cart is never
  // left half-done; the drawer stays open and re-renders in place.
  class PackUpgrade extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.onClick();
        }
      });
    }

    onClick() {
      if (this.hasAttribute('aria-busy')) return;
      this.setAttribute('aria-busy', 'true');
      this.classList.add('loading');

      const updates = {};
      updates[this.dataset.lineKey] = parseInt(this.dataset.singlesAfter, 10) || 0;
      updates[this.dataset.packVariantId] = 1;

      const body = JSON.stringify({
        updates,
        sections: sectionsPayload(),
        sections_url: window.location.pathname
      });

      fetch(theme.routes.cart_update_url, { ...fetchConfig('javascript'), body })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.status) throw new Error(parsedState.description || 'update failed');
          renderMiniCart(parsedState);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedState } }));
        })
        .catch((e) => {
          console.error(e);
          this.removeAttribute('aria-busy');
          this.classList.remove('loading');
        });
    }
  }

  customElements.define('pack-upgrade', PackUpgrade);

  // Terms checkbox before checkout (snippets/cart-terms.liquid). Delegated,
  // because the cart drawer is re-rendered on every change; the tick is kept
  // in sessionStorage so a quantity change doesn't clear it.
  const TERMS_KEY = 'o8-cart-terms';
  const termsAccepted = () => {
    try { return sessionStorage.getItem(TERMS_KEY) === '1'; } catch (e) { return false; }
  };
  const restoreTerms = () => {
    if (!termsAccepted()) return;
    document.querySelectorAll('[data-cart-terms-input]').forEach((input) => (input.checked = true));
  };

  document.addEventListener('change', (event) => {
    if (!event.target.matches('[data-cart-terms-input]')) return;
    const checked = event.target.checked;
    try { sessionStorage.setItem(TERMS_KEY, checked ? '1' : '0'); } catch (e) {}
    document.querySelectorAll('[data-cart-terms-input]').forEach((input) => (input.checked = checked));
    document.querySelectorAll('[data-cart-terms-error]').forEach((el) => (el.hidden = checked));
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[name="checkout"]');
    if (!button) return;
    const scope = button.closest('form, .cart__footer, .shopify-section') || document;
    const input = scope.querySelector('[data-cart-terms-input]') || document.querySelector('[data-cart-terms-input]');
    if (!input || input.checked) return;
    event.preventDefault();
    const box = input.closest('[data-cart-terms]');
    const error = box && box.querySelector('[data-cart-terms-error]');
    if (error) error.hidden = false;
    if (box) box.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, true);

  restoreTerms();
  const termsRoot = document.getElementById('mini-cart');
  if (termsRoot) new MutationObserver(restoreTerms).observe(termsRoot, { childList: true, subtree: true });

  fetchCart();

  // Changes made elsewhere (cart drawer, product page form, back button)
  // flow back into the cards.
  document.addEventListener('cart:updated', fetchCart);
  document.addEventListener('ajaxProduct:added', () => setTimeout(fetchCart, 0));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) fetchCart();
  });
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => fetchCart());
  }
})();
